import { collectPartnerProfile } from "../components/produktpartner/partnerProfileModel";
import type { FormState } from "./formUtils";
import { bankHatAngaben, kontoinhaberVorschlag } from "./kontoinhaber";
import {
  firmenIds,
  firmenNamen,
  geschaeftlicheFirmen,
  slotFirmen,
  weitereFirmen,
  type CommCompanies,
  type FirmaOption,
} from "./geschaeftlichFirma";
import { foListeAusForm } from "./foUserIds";
import { normalizePhone } from "../components/forms/validation/phone";
import { geschlechtAusAnrede } from "./anredeGeschlecht";
import { jahreseinkommenAusEingabe } from "./steuerUndAusweisAngaben";
import {
  herkunftDetailsFuerSpeichern,
  herkunftDetailsLesen,
  herkunftFreitextFuerSpeichern,
  herkunftZusammensetzen,
} from "./kontaktherkunft";
import { istRollenGruppe } from "../data/rollenKatalog";
import { rollenAusFeld, statusFuerApi } from "../data/kontaktstatusSegment";
import {
  zuwendungenAusFeld,
  zuwendungenFuerApi,
} from "../data/zuwendungenForm";
import { todesfallBelegeAusFeld, todesfallBelegeFuerApi } from "../data/todesfallBelege";
import { adresstypLabel, eigenerAnschriftName } from "../data/adresstypen";
import { eigenerNewsletterPayload } from "./eigenerNewsletter";
import { istRentner } from "../data/berufRentner";


/**
 * Builds the POST /contact payload for a NEW private (Person) contact from the
 * SinglePageForm `privat_contact_form` field state.
 *
 * Mirrors `buildCompanyContactCreatePayload` but for a person. Address / bank /
 * communication rows are nested directly — the Contact entity cascades them on
 * save, and `defaultAddress` / `defaultBankAccount` / `defaultPhone` are
 * projected from the child row with `isDefault=true` on read (the legacy default
 * FK columns were dropped), so a single POST is enough — no staged follow-up.
 *
 * Returns null when the required `lastname` is missing (validation failure).
 */

/**
 * Die im Formular als JSON abgelegte Herkunftsauswahl auslesen.
 *
 * `useContactOrigins` schreibt sie als JSON-Array in `contactOriginSelections`;
 * ein kaputter oder fehlender Wert darf das Anlegen nicht scheitern lassen —
 * dann geht die Herkunft eben leer mit.
 */
function herkunftZerlegenAusFormular(formFields: FormState): string[] {
  const roh = formFields.contactOriginSelections;
  if (typeof roh !== "string" || !roh.trim()) return [];
  try {
    const geparst: unknown = JSON.parse(roh);
    return Array.isArray(geparst) ? geparst.filter((v): v is string => typeof v === "string") : [];
  } catch {
    // Kaputter Wert heißt „keine Angabe", nicht „eine Herkunft namens {kein
    // json". Der Formularwert ist immer JSON (useContactOrigins schreibt ihn);
    // ein anderer Inhalt ist ein Fehler, kein Spaltenformat.
    return [];
  }
}

const RUFNUMMER_ARTEN = new Set(["phone", "mobile", "fax"]);

/**
 * Rufnummer eines weiteren Kommunikationswegs im KMVS-Format „+49 221 123456-78"
 * (T-59) — auch für Altentwürfe und Übernahmen, die das Popup nie gesehen haben.
 * Bestandszeilen (mit `dbId`) bleiben, wie sie sind: Speichern der Akte schreibt
 * keine alten Nummern still um, die Anzeige formatiert sie ohnehin (#1792).
 * Andere Arten und Freitext bleiben unverändert.
 */
export const wegWertFuerSpeichern = (
  type: string,
  value: string,
  geschaeftlich: boolean,
  bestand: boolean,
): string => (!bestand && RUFNUMMER_ARTEN.has(type) ? normalizePhone(value, { geschaeftlich }) || value : value);

/**
 * Vergleichsschlüssel gegen Doppelungen: „0241 4740799" und „+49 241 4740799"
 * sind dieselbe Nummer. Freitext/E-Mail/Links: klein geschrieben.
 */
export const wegVergleichsSchluessel = (value: string): string =>
  (normalizePhone(value, { geschaeftlich: false }) || value).trim().toLowerCase();

export type AddressPayload = {
  /** Datenbank-Id einer Bestandszeile — nur im Bearbeiten-Modus gesetzt. */
  id?: number;
  addressDescription: string;
  street: string;
  postalCode: string;
  city: string;
  ortsteil: string;
  country: string;
  /**
   * Was für eine Anschrift das ist (Zweitwohnsitz, Ferienimmobilie …).
   *
   * Ersetzt das frühere `type: "private"`: Das stand hier bis 09/2026, kam aber
   * NIE an — `ContactAddress` hatte kein solches Feld, TypeORM verwarf es
   * stillschweigend. Und es beantwortete eine andere Frage (privat oder
   * geschäftlich) als diese hier.
   */
  addressType?: string;
  isDefault: boolean;
};

export type CommunicationPayload = {
  /** Datenbank-Id einer Bestandszeile — nur im Bearbeiten-Modus gesetzt. */
  id?: number;
  type: string;
  label: string;
  /** Strukturierte Art (SP1): 'privat' | 'geschaeftlich'; fehlt bei Social/Firma. */
  kind?: string;
  value: string;
  isDefault: boolean;
  /** Erste Firma — Spiegel von `companyIds[0]` für Leser, die nur eine kennen. */
  companyId?: number | null;
  /** Alle Unternehmen eines geschäftlichen Wegs (seit 02.10.2026, n:m). */
  companyIds?: number[];
};

export type BankPayload = {
  /** Datenbank-Id einer Bestandszeile — nur im Bearbeiten-Modus gesetzt. */
  id?: number;
  accountHolder: string;
  iban: string;
  bic: string;
  bankName: string;
  bankDescription: string;
  isDefault: boolean;
};

const str = (value: unknown): string => (typeof value === "string" ? value.trim() : value != null ? String(value).trim() : "");

const firstNonEmpty = (...values: unknown[]): string => {
  for (const v of values) {
    const s = str(v);
    if (s) return s;
  }
  return "";
};

/**
 * Datenbank-Id einer Listenzeile aus dem Formular-State.
 *
 * Die Neuanlage kennt keine — dann bleibt alles wie bisher. Im Bearbeiten-Modus
 * trägt `contactToFormState` sie mit (`dbId` je Zeile, `addressDbId`,
 * `bankDbId`, `commDbIds`). Ohne sie legte jedes Speichern die Zeile neu an,
 * und Verweise auf die alte (Vertrag → Bankverbindung) zeigten ins Leere.
 */
export const dbIdAus = (v: unknown): number | undefined => {
  const n = Number(v);
  return v != null && v !== "" && Number.isInteger(n) && n > 0 ? n : undefined;
};

/** `commDbIds` (JSON: Feldname → Id) lesen. Unlesbares gilt als leer. */
export const commDbIdsAus = (roh: unknown): Record<string, number> => {
  if (typeof roh !== "string" || !roh.trim()) return {};
  try {
    const p = JSON.parse(roh);
    return p && typeof p === "object" && !Array.isArray(p) ? (p as Record<string, number>) : {};
  } catch {
    return {};
  }
};

/** Parse a form's JSON-encoded list field (additionalAddresses/Banks) into rows. */
const parseJsonArray = (raw: unknown): Record<string, unknown>[] => {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? (parsed.filter((x) => x && typeof x === "object") as Record<string, unknown>[])
      : [];
  } catch {
    return [];
  }
};

export type OwnershipPayload = {
  foUserId?: string;
  /** Alle FO (28.09.2026), erster = `foUserId`. Fehlt sie, gilt `formFields.foUserIds`. */
  foUserIds?: string[];
  boType?: "NONE" | "USER" | "TEAM";
  boTeamId?: string;
  boUserId?: string;
  inhouseContactUserId?: string;
};

/**
 * Mehrere FO (28.09.2026): `foUserIds` nur, wenn es einen primären FO gibt und
 * die Liste mit ihm beginnt — sonst entschiede der Server über eine Liste, die
 * nicht zum mitgeschickten `foUserId` passt.
 */
export function foUserIdsFuerPayload(
  formFields: FormState,
  ownership?: OwnershipPayload,
): { foUserIds?: string[] } {
  const primaer = ownership?.foUserId?.trim();
  if (!primaer) return {};
  const liste = ownership?.foUserIds?.length
    ? ownership.foUserIds
    : foListeAusForm(formFields as Record<string, unknown>);
  if (liste.length === 0 || liste[0] !== primaer) return { foUserIds: [primaer] };
  return { foUserIds: [...new Set(liste)] };
}

export const buildPrivateContactCreatePayload = (
  formFields: FormState,
  personTypeId?: number,
  ownership?: OwnershipPayload,
  /**
   * `allowIncomplete` für den UPDATE-Fall (Umwandlung Firma → Person) — siehe
   * die ausführliche Begründung in buildCompanyContactCreatePayload. Kurz: Der
   * Kontakt existiert bereits; ein `null` hier ließe den Aufrufer das Speichern
   * überspringen und trotzdem Erfolg melden.
   */
  opts?: {
    allowIncomplete?: boolean;
    /** `false`: keine automatische Firma für geschäftliche Wege (Ladestand der Akte). */
    firmaAutomatisch?: boolean;
  },
): Record<string, unknown> | null => {
  const lastname = str(formFields.lastname);
  if (!opts?.allowIncomplete && !lastname) return null;

  const firstName = str(formFields.firstname);
  const title = str(formFields.title);
  // Kontaktstatus (Interessent/Kunde/Sonstige). Bei „Sonstige" gilt der konkrete
  // Untertyp. Wird per Name an den Server gegeben (resolveRelations → statusName).
  // Das Feld ist im Anlage-Formular das ERSTE und UI-seitig auf „Interessent"
  // vorausgewählt — diese Vorauswahl wird aber nur bei aktivem Klick in den State
  // geschrieben. Damit NIE ein Kontakt ohne Status entsteht, den sichtbaren Default
  // hart absichern (Sonstige ohne Untertyp → „Sonstige", sonst → „Interessent").
  // Die angekreuzten Verfügungen stehen als JSON im Formularfeld — dieselbe
  // Form, die die Akte an den Server gibt. Unlesbares gilt als leer: Eine
  // kaputte Zeile darf das Anlegen nicht reißen.
  const verfuegungen = ((): Array<Record<string, unknown>> => {
    const roh = formFields.verfuegungen;
    if (Array.isArray(roh)) return roh as Array<Record<string, unknown>>;
    if (typeof roh !== "string" || !roh.trim()) return [];
    try {
      const p = JSON.parse(roh);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  })()
    .filter((v) => typeof v?.art === "string" && (v.art as string).trim())
    .map((v) => ({
      art: v.art,
      bevollmaechtigterContactId: (v.bevollmaechtigterContactId as number) ?? null,
      bevollmaechtigterName: (v.bevollmaechtigterName as string) ?? null,
    }));

  // Zuwendungen, Geschenke und Einladungen. Zeilen ohne „was" fallen heraus —
  // der Server verwürfe sie ohnehin, und eine leer hinzugefügte Zeile soll das
  // Speichern nicht mit einer stillen Verwerfung quittieren.
  const zuwendungen = zuwendungenFuerApi(zuwendungenAusFeld(formFields.zuwendungen));
  const todesfallBelege = todesfallBelegeFuerApi(todesfallBelegeAusFeld(formFields.todesfallBelege));

  /**
   * Eine ganze, positive Zahl — oder `null`.
   *
   * Leere Eingaben, Text und Unsinn dürfen nicht als 0 durchgehen: „0 g" wäre
   * eine Aussage, „nicht erfasst" ist keine.
   */
  const ganzeZahl = (v: unknown): number | null => {
    const s = String(v ?? "").trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const parseReferrerName = (raw: unknown): string => {
    if (typeof raw !== "string" || !raw.trim()) return "";
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return (
          parsed.displayName ||
          parsed.name ||
          `${parsed.vorname || ""} ${parsed.nachname || ""}`.trim() ||
          ""
        );
      }
    } catch {
      return String(raw).trim();
    }
    return "";
  };

  const empfehlungsgeber = parseReferrerName(formFields.referrerInteressent) || str(formFields.empfehlungsgeber);
  const istSelbstEmpfehlungsgeber =
    formFields.istEmpfehlungsgeber === true ||
    formFields.istEmpfehlungsgeber === "ja" ||
    formFields.istEmpfehlungsgeber === "true";

  const rawStatus = str(formFields.kontaktStatus);
  // Die Anlage-Maske schickt seit 09/2026 eine LISTE von Rollen
  // (`kontaktStatusRollen`), aus der ein Primär-Status und beliebig viele
  // Zusatz-Status werden — die Regel dazu steht in `statusFuerApi`.
  //
  // Der Rückfall darunter ist kein Altlast-Rest: `SinglePageForm` und
  // `SinglePageCompanyForm` (Akte) schicken weiterhin Untertyp + Rolle aus zwei
  // Dropdowns. Ohne ihn verlören sie beim Speichern ihren Status. Bei einer
  // Rollen-Gruppe („Rolle im Schaden" …) ist der Untertyp dort nur der Abzweig;
  // der Gruppenname selbst darf `statusName` nie erreichen, er steht in keinem
  // ContactStatus-Katalog und `resolveRelations` verwürfe ihn ersatzlos.
  const rollen = rollenAusFeld(str(formFields.kontaktStatusRollen));
  const altUntertyp = istRollenGruppe(str(formFields.kontaktStatusSonstig))
    ? str(formFields.kontaktStatusRolle)
    : str(formFields.kontaktStatusSonstig);
  const { statusName, weitereStatus: berechneteWeitereStatus } = statusFuerApi({
    kontaktStatus: rawStatus,
    rollen: rollen.length ? rollen : altUntertyp ? [altUntertyp] : [],
    auchKundeOderInteressent: str(formFields.kontaktStatusAuch),
  });

  const weitereStatus = [...berechneteWeitereStatus];
  if (istSelbstEmpfehlungsgeber && statusName !== "Empfehlungsgeber" && !weitereStatus.includes("Empfehlungsgeber")) {
    weitereStatus.push("Empfehlungsgeber");
  }

  // T1 — „persönlicher Kontakt": NUR als Schalter mitschicken. Die daraus
  // folgenden Ownership-Felder (createdByUserId, foUserId, ownerUserId,
  // boType, accessLevel) setzt ausschließlich der Server
  // (`ContactController.create`). Ein `false` wäre überflüssig — die Spalte
  // hat DEFAULT false — und würde im Umwandlungs-Modus, der denselben Builder
  // benutzt, nur unnötig mitreisen.
  const persoenlich = str(formFields.persoenlich) === "ja";

  // ── Communications ─────────────────────────────────────────────
  /** `companyIds` (alle) + `companyId` (erste) eines geschäftlichen Wegs. */
  const firmenFelder = (firmen: FirmaOption[]): { companyIds: number[]; companyId: number | null } => {
    const ids = firmenIds(firmen);
    return { companyIds: ids, companyId: ids[0] ?? null };
  };
  // (type, label, kind, companyId) triples in display order.
  const commCompanies: CommCompanies = (() => {
    try {
      const parsed = JSON.parse(String(formFields.commCompanies ?? "{}"));
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  })();
  // Genau eine aktive Firma → sie gehört immer dazu (geschaeftlichFirma.ts).
  // Abschaltbar, damit der Akten-Diff den Ladestand ohne Automatik bauen kann
  // (anlageUpdatePayload.ts): ohne Firmenliste gilt nur, was gespeichert ist.
  const firmenListe = opts?.firmaAutomatisch === false ? [] : geschaeftlicheFirmen(formFields);

  /** Wirksame Firmen eines festen Felds: gewählt (bzw. Altfeld) + die einzige aktive. */
  const workFirmen = (key: string): FirmaOption[] => {
    const altName = str(formFields[`${key}CompanyName`]);
    if (!Object.prototype.hasOwnProperty.call(commCompanies, key) && altName) {
      const n = Number(formFields[`${key}CompanyId`]);
      return slotFirmen(
        { [key]: Number.isInteger(n) && n > 0 ? { id: n, name: altName } : { name: altName } },
        key,
        firmenListe,
      );
    }
    return slotFirmen(commCompanies, key, firmenListe);
  };

  /** „Geschäftlich (A, B)" — die Firmen im Label, wie bisher die eine. */
  const mitFirmen = (basis: string, firmen: FirmaOption[]): string => {
    const namen = firmenNamen(firmen);
    return namen.length ? `${basis} (${namen.join(", ")})` : basis;
  };

  const commSpecs: Array<{ feld: string; type: string; label: string; kind?: string; firmen?: FirmaOption[]; value: string }> = [
    { feld: "phonePrivate", type: "phone", label: "Privat", kind: "privat", value: str(formFields.phonePrivate) },
    {
      feld: "phoneWork",
      type: "phone",
      label: mitFirmen("Geschäftlich", workFirmen("phoneWork")),
      kind: "geschaeftlich",
      firmen: workFirmen("phoneWork"),
      value: str(formFields.phoneWork),
    },
    { feld: "mobilePrivate", type: "mobile", label: "Privat", kind: "privat", value: str(formFields.mobilePrivate) },
    {
      feld: "mobileWork",
      type: "mobile",
      label: mitFirmen("Geschäftlich", workFirmen("mobileWork")),
      kind: "geschaeftlich",
      firmen: workFirmen("mobileWork"),
      value: str(formFields.mobileWork),
    },
    { feld: "emailPrivate", type: "email", label: "Privat", kind: "privat", value: str(formFields.emailPrivate) },
    {
      feld: "emailWork",
      type: "email",
      label: mitFirmen("Geschäftlich", workFirmen("emailWork")),
      kind: "geschaeftlich",
      firmen: workFirmen("emailWork"),
      value: str(formFields.emailWork),
    },
    { feld: "linkedin", type: "linkedin", label: "LinkedIn", value: str(formFields.linkedin) },
    { feld: "instagram", type: "instagram", label: "Instagram", value: str(formFields.instagram) },
    { feld: "facebook", type: "facebook", label: "Facebook", value: str(formFields.facebook) },
    { feld: "tiktok", type: "tiktok", label: "TikTok", value: str(formFields.tiktok) },
    { feld: "xing", type: "xing", label: "XING", value: str(formFields.xing) },
    { feld: "youtube", type: "youtube", label: "YouTube", value: str(formFields.youtube) },
  ];
  const commIds = commDbIdsAus(formFields.commDbIds);
  const isPhoneLike = (t: string) => t === "phone" || t === "mobile";
  let phoneDefaultSet = false;
  let emailDefaultSet = false;
  const communications: CommunicationPayload[] = [];
  for (const c of commSpecs) {
    if (!c.value) continue;
    let isDefault = false;
    if (isPhoneLike(c.type) && !phoneDefaultSet) {
      isDefault = true;
      phoneDefaultSet = true;
    } else if (c.type === "email" && !emailDefaultSet) {
      isDefault = true;
      emailDefaultSet = true;
    }
    const id = dbIdAus(commIds[c.feld]);
    communications.push({
      ...(id ? { id } : {}),
      type: c.type,
      label: c.label,
      ...(c.kind ? { kind: c.kind } : {}),
      // Geschäftlich: IMMER die Liste (auch leer) — so räumt das Speichern eine
      // abgewählte Zuordnung wirklich ab, und der Server ordnet nicht
      // eigenmächtig zu (contactKommunikationFirma.ts). `companyId` als Spiegel.
      ...(c.kind === "geschaeftlich" ? firmenFelder(c.firmen ?? []) : {}),
      value: c.value,
      isDefault,
    });
  }

  // Zusätzliche Kommunikationswege aus dem Dialog
  const ERLAUBTE_COMM_TYPEN = new Set([
    "phone",
    "fax",
    "email",
    "mobile",
    "linkedin",
    "instagram",
    "facebook",
    "tiktok",
    "xing",
    "youtube",
    "homepage",
  ]);
  const belegteWerte = new Set(communications.map((c) => wegVergleichsSchluessel(c.value)));
  const extraCommsRaw = parseJsonArray(formFields.weitereKommunikation);
  for (const item of extraCommsRaw) {
    const rawType = str(item.type);
    const kind = str(item.zuordnung || item.kind);
    const value = wegWertFuerSpeichern(rawType, str(item.value), kind !== "privat", dbIdAus(item.dbId) !== undefined);
    const firmen = weitereFirmen(item, firmenListe);
    const rawCompName = firmenNamen(firmen).join(", ");
    // Ohne eigene Bezeichnung heißt der Weg nach seiner Spalte, sonst nach der Art.
    const defaultTypeName =
      kind === "geschaeftlich"
        ? "Geschäftlich"
        : kind === "privat"
          ? "Privat"
          : rawType === "mobile" ? "Mobil" : rawType === "fax" ? "Fax" : rawType === "email" ? "E-Mail" : "Telefon";
    let label = str(item.label);
    if (!label) {
      label = rawCompName ? `${defaultTypeName} (${rawCompName})` : defaultTypeName;
    } else if (rawCompName && !label.includes(rawCompName)) {
      label = `${label} (${rawCompName})`;
    }
    if (!value) continue;
    const type = rawType === "website" ? "homepage" : rawType;
    if (!ERLAUBTE_COMM_TYPEN.has(type)) continue;
    const extraId = dbIdAus(item.dbId);
    // Bestandszeilen (mit dbId) nie als Doppel aussortieren: Fehlt eine Zeile
    // mit id in der Liste, LÖSCHT das Backend sie. Entdoppelt werden nur neue
    // Einträge (T-59-Nachtrag, 01.10.2026).
    const schluessel = wegVergleichsSchluessel(value);
    if (!extraId && belegteWerte.has(schluessel)) continue;
    belegteWerte.add(schluessel);
    communications.push({
      ...(extraId ? { id: extraId } : {}),
      type,
      label,
      value,
      isDefault: false,
      ...(kind ? { kind } : {}),
      ...(kind === "geschaeftlich" ? firmenFelder(firmen) : {}),
    });
  }

  // ── Erreichbarkeit ─────────────────────────────────────────────
  const erreichbarkeitAllg = str(formFields.erreichbarkeit);
  const detailErreichbarkeiten: Record<string, string> = {};
  try {
    const rawDetails = JSON.parse(String(formFields.erreichbarkeitenDetail ?? "{}"));
    if (typeof rawDetails === "object" && rawDetails !== null) {
      Object.assign(detailErreichbarkeiten, rawDetails);
    }
  } catch {
    // ignore
  }
  const detailParts: string[] = [];
  const fieldLabels: Record<string, string> = {
    phonePrivate: "Telefon (privat)",
    mobilePrivate: "Mobil (privat)",
    phoneWork: "Telefon (geschäftlich)",
    mobileWork: "Mobil (geschäftlich)",
  };
  for (const [k, v] of Object.entries(detailErreichbarkeiten)) {
    if (typeof v === "string" && v.trim()) {
      detailParts.push(`${fieldLabels[k] || k}: ${v.trim()}`);
    }
  }
  for (const item of extraCommsRaw) {
    if (item.erreichbarkeit && str(item.erreichbarkeit).trim()) {
      const lbl = str(item.label) || "Weitere Nummer";
      detailParts.push(`${lbl}: ${str(item.erreichbarkeit).trim()}`);
    }
  }
  const summaryParts: string[] = [];
  if (erreichbarkeitAllg) summaryParts.push(erreichbarkeitAllg);
  if (detailParts.length > 0) summaryParts.push(detailParts.join("; "));
  const erreichbarkeit = summaryParts.length > 0 ? summaryParts.join(" | ").substring(0, 255) : undefined;

  // ── Address ────────────────────────────────────────────────────

  const street = str(formFields.street);
  const postalCode = firstNonEmpty(formFields.postalcode, formFields.postalCode, formFields.plz);
  const city = firstNonEmpty(formFields.city, formFields.ort);
  const ortsteil = firstNonEmpty(formFields.cityArea, formFields.ortsteil);
  const country = firstNonEmpty(formFields.country, formFields.land);
  const hasAddress = [street, postalCode, city, ortsteil, country].some(Boolean);
  // Zweitwohnsitz / abweichende Postanschriften (mehrfach, ohne Standard-Flag).
  // Wurden bisher nicht persistiert.
  // Das Land (vorbelegt „Deutschland") allein macht noch keine Anschrift.
  const additionalAddresses: AddressPayload[] = parseJsonArray(formFields.additionalAddresses)
    .filter((a) => [a.street, a.postalCode, a.postalcode, a.plz, a.city, a.ort, a.ortsteil, a.cityArea].some((v) => str(v)))
    .map((a) => ({
      ...(dbIdAus(a.dbId) ? { id: dbIdAus(a.dbId) } : {}),
      // Eigener Name („Hanselhof"), sonst die Art-Beschriftung, sonst
      // „Weitere Anschrift". Eine früher automatisch gesetzte Beschriftung ist
      // KEIN Name: Wechselt die Art, folgt die Beschriftung ihr (T-358).
      addressDescription:
        eigenerAnschriftName(str(a.addressDescription)) ||
        (str(a.addressType) ? adresstypLabel(str(a.addressType)) : "Weitere Anschrift"),

      street: str(a.street),
      postalCode: firstNonEmpty(a.postalCode, a.postalcode, a.plz),
      city: firstNonEmpty(a.city, a.ort),
      ortsteil: firstNonEmpty(a.ortsteil, a.cityArea),
      country: firstNonEmpty(a.country, a.land),
      // `addressType` sagt, WAS die Anschrift ist (Zweitwohnsitz,
      // Ferienimmobilie …). Das frühere `type: "private"` stand hier bis
      // 09/2026 und kam nie an: `ContactAddress` hatte kein solches Feld,
      // TypeORM verwarf es stillschweigend.
      ...(str(a.addressType) ? { addressType: str(a.addressType) } : {}),
      isDefault: false,
    }));

  const addresses: AddressPayload[] = [
    ...(hasAddress
      ? [
          {
            ...(dbIdAus(formFields.addressDbId) ? { id: dbIdAus(formFields.addressDbId) } : {}),
            addressDescription: "Wohnanschrift",
            street,
            postalCode,
            city,
            ortsteil,
            country,
            // Die Standardadresse braucht keinen Typ: Sie IST der Erstwohnsitz,
            // gekennzeichnet über `isDefault`.
            isDefault: true,
          },
        ]
      : []),
    ...additionalAddresses,
  ];

  // ── Bank ───────────────────────────────────────────────────────
  const iban = str(formFields.iban);
  const bic = str(formFields.bic);
  const bankName = str(formFields.bankname);
  // Kontoinhaber: was getippt wurde, sonst der Name der Person — der wird seit
  // 02.10.2026 wirklich GESPEICHERT (vorher stand er nur im Feld, und 2.100
  // Bankverbindungen blieben ohne Inhaber). Ein leeres Feld heißt in der Maske
  // „noch nicht angefasst", der Vorschlag folgt dort jeder Namensänderung.
  const kontoinhaberDefault = kontoinhaberVorschlag(
    { firstName: str(formFields.firstname), lastname: str(formFields.lastname) },
    false,
  );
  const accountHolder = str(formFields.accountHolder) || kontoinhaberDefault;
  const bankDescription = str(formFields.bankbezeichnung);
  // Der vorbelegte Kontoinhaber allein macht noch keine Bankverbindung.
  const hasBank = bankHatAngaben([iban, bic, bankName], str(formFields.accountHolder), kontoinhaberDefault);
  // Weitere Bankverbindungen (mehrfach) — wurden bisher nicht persistiert.
  const additionalBanks: BankPayload[] = parseJsonArray(formFields.additionalBanks)
    .filter((b) => bankHatAngaben([b.iban, b.bic, b.bankName, b.bankname], str(b.accountHolder), kontoinhaberDefault))
    .map((b) => ({
      ...(dbIdAus(b.dbId) ? { id: dbIdAus(b.dbId) } : {}),
      accountHolder: str(b.accountHolder) || kontoinhaberDefault,
      iban: str(b.iban),
      bic: str(b.bic),
      bankName: firstNonEmpty(b.bankName, b.bankname),
      bankDescription: firstNonEmpty(b.bankDescription, b.label, b.bankbezeichnung),
      isDefault: false,
    }));

  const bankAccounts: BankPayload[] = [
    ...(hasBank
      ? [
          {
            ...(dbIdAus(formFields.bankDbId) ? { id: dbIdAus(formFields.bankDbId) } : {}),
            accountHolder,
            iban,
            bic,
            bankName,
            bankDescription,
            isDefault: true,
          },
        ]
      : []),
    ...additionalBanks,
  ];
  if (bankAccounts.length && !bankAccounts.some((b) => b.isDefault)) {
    bankAccounts[0].isDefault = true;
  }

  // Die Herkunftsauswahl liegt im Formular als JSON-Liste
  // (`contactOriginSelections`, geschrieben von useContactOrigins) — gespeichert
  // wird sie wie die übrigen Mehrfachfelder als „a; b".
  const herkunft = herkunftZusammensetzen(herkunftZerlegenAusFormular(formFields));
  const herkunftSonstiges = herkunftFreitextFuerSpeichern(
    herkunftZerlegenAusFormular(formFields),
    str(formFields.contactOriginOther),
  );
  const herkunftDetails = herkunftDetailsFuerSpeichern(
    herkunftZerlegenAusFormular(formFields),
    herkunftDetailsLesen(formFields.kontaktherkunftDetails),
  );

  const payload: Record<string, unknown> = {
    lastname,
    displayName: firstName ? `${lastname}, ${firstName}` : lastname,
    ...(firstName ? { firstName } : {}),
    // Scalar Person columns (keys match the Contact entity 1:1).
    ...(str(formFields.placeofbirth) ? { birthPlace: str(formFields.placeofbirth) } : {}),
    ...(str(formFields.countryofbirth) ? { birthCountry: str(formFields.countryofbirth) } : {}),
    ...(str(formFields.dateofbirth) ? { birthDate: str(formFields.dateofbirth) } : {}),
    ...(str(formFields.birthname) ? { birthName: str(formFields.birthname) } : {}),
    ...(str(formFields.citizenship) ? { nationality: str(formFields.citizenship) } : {}),
    // WICHTIG: Entity-PROPERTY-Namen senden (jobIndustry/jobOccupation/jobEmployer),
    // NICHT die Spaltennamen (industry/occupation/employer) — TypeORM mappt per
    // Property, sonst werden Beruf/Branche/Arbeitgeber still verworfen.
    ...(str(formFields.occupationStatus) ? { jobStatus: str(formFields.occupationStatus) } : {}),
    ...(str(formFields.employer) ? { jobEmployer: str(formFields.employer) } : {}),
    ...(str(formFields.industry) ? { jobIndustry: str(formFields.industry) } : {}),
    ...(str(formFields.position) ? { jobOccupation: str(formFields.position) } : {}),
    // Geschützte Angaben (Steuerklasse, Jahreseinkommen, Personalausweis). Der
    // Server nimmt sie NUR an, wenn der Anlegende für den neuen Kontakt auch
    // verantwortlich ist (FO/BO) bzw. gfgl/admin ist — sonst fallen sie still
    // weg. Das ist gewollt: Die Regel darf nicht davon abhängen, welche Maske
    // schreibt. Hier stehen sie, damit die Anlage sie nicht von vornherein
    // verliert.
    ...(str(formFields.taxClass) ? { taxClass: str(formFields.taxClass) } : {}),
    ...(jahreseinkommenAusEingabe(str(formFields.annualIncome)) !== null
      ? { annualIncome: jahreseinkommenAusEingabe(str(formFields.annualIncome)) }
      : {}),
    ...(str(formFields.idCardNumber) ? { idCardNumber: str(formFields.idCardNumber) } : {}),
    ...(str(formFields.socialSecurityNumber)
      ? { socialSecurityNumber: str(formFields.socialSecurityNumber) }
      : {}),
    // Ein leeres Datumsfeld liefert "" — und "" ist für eine date-Spalte kein
    // Datum (22007). Deshalb nur senden, wenn wirklich etwas dasteht.
    ...(str(formFields.idCardValidUntil)
      ? { idCardValidUntil: str(formFields.idCardValidUntil) }
      : {}),
    // Die übrigen Ausweisdaten und die Steuer-ID — geschützt wie die Nummer,
    // und aus demselben Grund nur senden, wenn gefüllt.
    ...(str(formFields.idCardIssuedAt)
      ? { idCardIssuedAt: str(formFields.idCardIssuedAt) }
      : {}),
    ...(str(formFields.idCardAuthority) ? { idCardAuthority: str(formFields.idCardAuthority) } : {}),
    ...(str(formFields.idCardType) ? { idCardType: str(formFields.idCardType) } : {}),
    ...(str(formFields.idCardSubtype) ? { idCardSubtype: str(formFields.idCardSubtype) } : {}),
    ...(str(formFields.idCardClasses) ? { idCardClasses: str(formFields.idCardClasses) } : {}),
    ...(str(formFields.additionalDocuments) ? { additionalDocuments: str(formFields.additionalDocuments) } : {}),
    ...(str(formFields.taxIdNumber) ? { taxIdNumber: str(formFields.taxIdNumber) } : {}),
    ...(str(formFields.taxNumber) ? { taxNumber: str(formFields.taxNumber) } : {}),
    // Title resolves by name server-side (no-op if no match).
    ...(title ? { titleName: title } : {}),
    // Kontaktstatus IMMER senden (Default „Interessent"), damit kein statusloser
    // Kontakt entsteht.
    statusName,
    // Nur mitschicken, wenn es etwas mitzuteilen gibt: `POST /contact`
    // prüft die Liste gegen den Katalog und lehnt Unbekanntes mit 400 ab.
    ...(weitereStatus.length ? { weitereStatus } : {}),
    // Anrede und Geschlecht. Bis 08/2026 trug das Formularfeld „anrede" den
    // GESCHLECHTSTEXT („Männlich"/„Weiblich"); jetzt trägt es die echte Anrede
    // („Herr"/„Frau"), die das Backend über `salutationName` auflöst. Das
    // Geschlecht wird daraus abgeleitet und weiterhin mitgeschickt — die
    // Verwandtschaftsableitung liest es.
    ...(str(formFields.anrede)
      ? {
          salutationName: str(formFields.anrede),
          ...(geschlechtAusAnrede(str(formFields.anrede))
            ? { gender: geschlechtAusAnrede(str(formFields.anrede)) }
            : {}),
        }
      : {}),
    // Rufname → Contact.callName (the form writes it to the "nickname" field).
    ...(str(formFields.nickname) ? { callName: str(formFields.nickname) } : {}),
    // Familienstand → Contact.maritalStatus. Free-text "Sonstiges" value
    // (familyStatusOther) wins over the dropdown sentinel when present.
    ...((str(formFields.familyStatusOther) || str(formFields.familyStatus))
      ? { maritalStatus: str(formFields.familyStatusOther) || str(formFields.familyStatus) }
      : {}),
    // Bemerkung → Contact.remark (Freitext).
    ...(str(formFields.remark) ? { remark: str(formFields.remark) } : {}),
    // Wie der Kontakt zustande gekommen ist → Contact.kontaktherkunft. Bis
    // 09/2026 blieb von der Frage nur ihre Wirkung übrig (Empfehlungsgeber als
    // Name, Tippgeber/Kundenverbindung als Verknüpfung); die Antwort selbst war
    // danach nicht mehr feststellbar und ließ sich in der Akte nicht zeigen.
    ...(herkunft ? { kontaktherkunft: herkunft } : {}),
    ...(herkunftSonstiges ? { kontaktherkunftSonstiges: herkunftSonstiges } : {}),
    ...(herkunftDetails ? { kontaktherkunftDetails: herkunftDetails } : {}),
    ...(empfehlungsgeber ? { empfehlungsgeber } : {}),
    // Hobbys und Vorlieben → getrennte Freitextspalten (Chips, kommagetrennt).
    ...(str(formFields.hobbys) ? { hobbys: str(formFields.hobbys) } : {}),
    ...(str(formFields.vorlieben) ? { vorlieben: str(formFields.vorlieben) } : {}),
    // Erreichbarkeit
    ...(erreichbarkeit ? { erreichbarkeit } : {}),
    // Mitgliedschaften → Contact.memberships (varchar).
    ...(str(formFields.memberships) ? { memberships: str(formFields.memberships) } : {}),

    // Verfügungen (Vorsorgevollmacht, Patientenverfügung …) — eine Kindtabelle,
    // die NICHT über den Cascade-Pfad läuft (contactVerfuegung.write.ts). Der
    // Server nimmt sie beim Anlegen genauso entgegen wie beim Ändern; bis
    // 09/2026 konnte nur die Akte sie schreiben.
    ...(verfuegungen.length ? { verfuegungen } : {}),
    // Verstorben: Das Flag führt, das Datum ergänzt. Ein Datum ohne Flag gäbe
    // es nur aus Altbeständen — die Migration hat sie nachgezogen.
    // Die Belege zum Todesfall (T-357) hängen mit am Flag: Sie sind die
    // Nachweise FÜR den Todesfall. Ohne „ja“ bleiben sie weg, auch wenn im
    // Formular noch hochgeladene Dateien stehen — sonst entstünde ein lebender
    // Kontakt mit Sterbeanzeige. Die Maske räumt sie deshalb beim
    // Zurückschalten NICHT weg: Wer versehentlich umschaltet, verliert seine
    // Uploads nicht, und die Regel steht an genau einer Stelle — hier.
    ...(str(formFields.deceased) === "ja"
      ? {
          deceased: true,
          ...(str(formFields.deceasedAt) ? { deceasedAt: str(formFields.deceasedAt) } : {}),
          ...(todesfallBelege.length ? { todesfallBelege } : {}),
        }
      : {}),
    // Ansprache: Vorselektion und Newsletter — beide nur mitschicken, wenn
    // gesetzt. Ein `newsletter: false` wäre bei einer Umwandlung eine echte
    // Aussage („kein Newsletter") und würde eine bestehende überschreiben.
    ...(str(formFields.einladungKennzeichen)
      ? { einladungKennzeichen: str(formFields.einladungKennzeichen) }
      : {}),
    ...(str(formFields.newsletter) === "ja" ? { newsletter: true } : {}),
    ...(str(formFields.newsletter) === "ja" && str(formFields.newsletterEmails)
      ? { newsletterEmails: str(formFields.newsletterEmails) }
      : {}),
    // Eigener Newsletter des Kontakts (User 28.09.2026).
    ...eigenerNewsletterPayload(formFields),
    ...(zuwendungen.length ? { zuwendungen } : {}),
    // Geburtsangaben und Hochzeitstage — jeweils nur mitschicken, wenn
    // gefüllt. Zahlen gehen als ZAHL: Ein "3480" aus dem Eingabefeld wäre für
    // die int-Spalte ein String und liefe beim Anlegen in einen Typfehler.
    ...(str(formFields.birthTime) ? { birthTime: str(formFields.birthTime) } : {}),
    ...(ganzeZahl(formFields.birthWeightGrams) != null
      ? { birthWeightGrams: ganzeZahl(formFields.birthWeightGrams) }
      : {}),
    ...(ganzeZahl(formFields.birthHeightCm) != null
      ? { birthHeightCm: ganzeZahl(formFields.birthHeightCm) }
      : {}),
    ...(str(formFields.weddingDateCivil) ? { weddingDateCivil: str(formFields.weddingDateCivil) } : {}),
    ...(str(formFields.weddingDateChurch) ? { weddingDateChurch: str(formFields.weddingDateChurch) } : {}),
    ...(str(formFields.zusaetzlichSelbststaendig)
      ? { zusaetzlichSelbststaendig: str(formFields.zusaetzlichSelbststaendig) }
      : {}),
    // Rentner mit weiterer Tätigkeit: 'angestellt' | 'selbststaendig'
    // (data/berufRentner.ts). Nur beim Status Rentner — die Maske leert das
    // Feld beim Statuswechsel, das hier ist die zweite Sicherung.
    ...(str(formFields.weitereTaetigkeit) && istRentner(str(formFields.occupationStatus))
      ? { weitereTaetigkeit: str(formFields.weitereTaetigkeit) }
      : {}),
    ...(personTypeId ? { type: { id: personTypeId } } : {}),
    ...(addresses.length ? { addresses } : {}),
    ...(communications.length ? { communications } : {}),
    ...(bankAccounts.length ? { bankAccounts } : {}),
    ...(persoenlich ? { persoenlich: true } : {}),
    // ── Verantwortlichkeit (RBAC) ──────────────────────────────────────
    // Ohne foUserId macht das Backend für nicht-gfgl-Caller accessLevel=TENANT
    // (für alle sichtbar). Daher wird foUserId beim Anlegen vorbelegt (s. Form).
    ...(ownership?.foUserId ? { foUserId: ownership.foUserId } : {}),
    ...foUserIdsFuerPayload(formFields, ownership),
    ...(ownership?.boType ? { boType: ownership.boType } : {}),
    ...(ownership?.boType === "TEAM" && ownership.boTeamId
      ? { boTeamId: ownership.boTeamId }
      : {}),
    ...(ownership?.boType === "USER" && ownership.boUserId
      ? { boUserId: ownership.boUserId }
      : {}),
    ...(ownership?.inhouseContactUserId
      ? { inhouseContactUserId: ownership.inhouseContactUserId }
      : str(formFields.inhouseContactUserId)
      ? { inhouseContactUserId: str(formFields.inhouseContactUserId) }
      : {}),
  };

  if (statusName === "Ansprechpartner Produktpartner" || statusName === "Ansprechpartner Kooperationspartner") {
    payload.partnerProfile = collectPartnerProfile(key => str(formFields[key]));
  }
  return payload;
};
